import PageHeader from '@components/common/PageHeader';
import { ActionIcon, Group, Modal, SegmentedControl, Tabs, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { GearSix, Package } from '@phosphor-icons/react';
import { useCallback, useEffect, useState, type FC } from 'react';
import { useSearchParams } from 'react-router';

import { getChannelFulfillmentModes } from '@api/channel';
import type { AvailableChannel, DistributionChannelHealth, DistributionPlanSummary, PlanSettingsOverride } from '@api/distribution';
import { deleteDistributionPlan, getDistributionChannels, getDistributionPlanList } from '@api/distribution';
import DistributionPathsTab from '@components/containers/rebalance/DistributionPathsTab';
import { showError, showSuccess } from '@utils/notifications';

import ChannelDetailView from './ChannelDetailView';
import DistributionOverview from './DistributionOverview';
import DistributionPlanView, { type PlanComputeParams } from './DistributionPlanView';
import PlanListView from './PlanListView';
import PoChannelDetailView from './PoChannelDetailView';
import PoDrivenOverview from './PoDrivenOverview';
import TrackView from './TrackView';

// ─── View state machine ────────────────────────────────────────────────────────

type TopTab = 'plan' | 'track';
type PlanSubTab = 'push' | 'poDriven';

type ChannelView =
    | { mode: 'overview' }
    | { mode: 'channel'; channel: DistributionChannelHealth }
    | { mode: 'poChannel'; channel: AvailableChannel }
    | { mode: 'newPlan'; channel: DistributionChannelHealth; params: PlanComputeParams }
    | { mode: 'plansNewPlan'; params: PlanComputeParams }
    | { mode: 'existingPlan'; planId: string; planName?: string; channelKey?: string; shipmentCode?: string; fromAlertId?: string };

// ─── Container ────────────────────────────────────────────────────────────────

const DistributionContainer: FC = () => {
    // Top tab is URL-driven so the navbar's Distribution → Plan / Track sub-items control it.
    const [searchParams, setSearchParams] = useSearchParams();
    const topTab: TopTab = searchParams.get('view') === 'track' ? 'track' : 'plan';
    const viewParam = searchParams.get('view');
    const [planSubTab, setPlanSubTab] = useState<PlanSubTab>('push');
    // When set, the plan-list screen for that channel set is shown in place of the sub-tab overview.
    const [planListFor, setPlanListFor] = useState<PlanSubTab | null>(null);
    const [channelView, setChannelView] = useState<ChannelView>({ mode: 'overview' });

    const [plans, setPlans] = useState<DistributionPlanSummary[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [settingsOpen, { open: openSettings, close: closeSettings }] = useDisclosure(false);
    const [channels, setChannels] = useState<AvailableChannel[]>([]);
    const [channelsLoading, setChannelsLoading] = useState(true);
    const [poChannelKeys, setPoChannelKeys] = useState<Set<string>>(new Set());
    const [newPlanChannelKey, setNewPlanChannelKey] = useState<string | null>(null);

    const loadPlans = useCallback(() => {
        setPlansLoading(true);
        getDistributionPlanList(100, ['DRAFT'])
            .then(data => setPlans(data))
            .catch(() => setPlans([]))
            .finally(() => setPlansLoading(false));
    }, []);

    useEffect(() => {
        loadPlans();
        getDistributionChannels()
            .then(setChannels)
            .catch(() => {})
            .finally(() => setChannelsLoading(false));
        getChannelFulfillmentModes()
            .then(modes => setPoChannelKeys(
                new Set(modes.filter(m => m.fulfillmentMode === 'PO_DRIVEN').map(m => m.channelAbbreviationKey)),
            ))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // When the top tab changes via the URL (navbar Plan/Track), exit any fullscreen plan/channel view.
    useEffect(() => {
        setChannelView({ mode: 'overview' });
        setPlanListFor(null);
    }, [viewParam]);

    // Channels + draft plans split by fulfilment mode.
    const pushChannels = channels.filter(c => !poChannelKeys.has(c.channelAbbreviationKey));
    const poChannels = channels.filter(c => poChannelKeys.has(c.channelAbbreviationKey));
    const draftPlans = plans.filter(p => p.status === 'DRAFT');
    const pushDrafts = draftPlans.filter(p => !poChannelKeys.has(p.channelAbbreviationKey));
    const poDrafts = draftPlans.filter(p => poChannelKeys.has(p.channelAbbreviationKey));

    // ── Navigation ──

    const openExistingPlan = (planId: string, planName?: string, channelKey?: string, shipmentCode?: string, fromAlertId?: string) => {
        setChannelView({ mode: 'existingPlan', planId, planName, channelKey, shipmentCode, fromAlertId });
    };

    // Top-right Plan/Track mode toggle — persisted in the URL (?view=).
    const switchMode = (tab: TopTab) => {
        setPlanListFor(null);
        if (tab === 'plan') loadPlans();
        const next = new URLSearchParams(searchParams);
        next.set('view', tab);
        setSearchParams(next, { replace: true });
    };

    const handlePlanDeleted = () => {
        loadPlans();
        setChannelView({ mode: 'overview' });
    };

    const handleStartNewPlanFromTab = () => {
        if (!newPlanChannelKey) return;
        const ch = channels.find(c => c.channelAbbreviationKey === newPlanChannelKey);
        if (!ch) return;
        setPlanListFor(null);
        setChannelView({
            mode: 'plansNewPlan',
            params: {
                channelId: ch.channelAbbreviationKey,
                channelName: ch.channelName,
                channelAbbreviationKey: ch.channelAbbreviationKey,
                demandSource: ch.config?.demandSource ?? 'FORECAST',
                targetDohDays: ch.config?.targetDohDays ?? null,
                channelTierTargetDohDays: ch.config?.tierTargetDohDays ?? null,
                isAggregated: ch.config?.isAggregated ?? false,
            },
        });
    };

    // PO detail → "Create plan from this PO": open pre-filled plan settings (NEW plan, OPEN_PO demand,
    // PO's destination FC pre-selected for per-FC routing). User reviews settings and computes.
    const handleCreatePlanFromPo = (channelKey: string, purchaseOrderId: string, destinationWarehouseId: string | null) => {
        const ch = channels.find(c => c.channelAbbreviationKey === channelKey);
        if (!ch) return;
        const isAggregated = ch.config?.isAggregated ?? false;
        setChannelView({
            mode: 'plansNewPlan',
            params: {
                channelId: ch.channelAbbreviationKey,
                channelName: ch.channelName,
                channelAbbreviationKey: ch.channelAbbreviationKey,
                demandSource: 'OPEN_PO',
                targetDohDays: ch.config?.targetDohDays ?? null,
                channelTierTargetDohDays: ch.config?.tierTargetDohDays ?? null,
                isAggregated,
                purchaseOrderId,
                preselectedWarehouseIds: !isAggregated && destinationWarehouseId ? [destinationWarehouseId] : null,
            },
        });
    };

    // Deep link from a sales order's Fulfil button (docs/prds/sales-orders-v1.md, section 7.6):
    // /distribution?view=track&channel=<key>&po=<id>[&fc=<warehouseId>] opens the same pre-filled
    // plan builder once the channels are known, then drops the params so a refresh does not
    // re-open it. The handler only sets view state; nothing is written until the user computes
    // and approves, so a repeat call is harmless.
    const deepLinkPo = searchParams.get('po');
    const deepLinkChannel = searchParams.get('channel');
    const deepLinkFc = searchParams.get('fc');
    useEffect(() => {
        if (!deepLinkPo || !deepLinkChannel || channelsLoading) return;
        handleCreatePlanFromPo(deepLinkChannel, deepLinkPo, deepLinkFc);
        const next = new URLSearchParams(searchParams);
        next.delete('po');
        next.delete('channel');
        next.delete('fc');
        setSearchParams(next, { replace: true });
        // handleCreatePlanFromPo is re-created every render; the guards above make re-runs no-ops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkPo, deepLinkChannel, deepLinkFc, channelsLoading]);

    // Deep link to a saved plan: /distribution?view=plan&planId=<id> (a sales order's "Open plan",
    // or a scheduled plan's Home card, which adds &from=<checklist item id> so the view can show
    // where it came from). Opens that plan, then drops the params.
    const deepLinkPlanId = searchParams.get('planId');
    const deepLinkFrom = searchParams.get('from');
    useEffect(() => {
        if (!deepLinkPlanId) return;
        openExistingPlan(deepLinkPlanId, undefined, undefined, undefined, deepLinkFrom ?? undefined);
        const next = new URLSearchParams(searchParams);
        next.delete('planId');
        next.delete('from');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkPlanId]);

    // Deep link from the channel-understock alert's "Plan these SKUs" button:
    // /distribution?view=plan&channel=<key>&skus=<id,id,…> opens the plan builder for that channel
    // scoped to those SKUs (same settings page, same compute, same review/approve flow), then drops
    // the params so a refresh does not re-open it.
    const deepLinkSkus = searchParams.get('skus');
    const deepLinkAutoCompute = searchParams.get('autocompute') === '1';
    const deepLinkPlanSettings = searchParams.get('ps');
    useEffect(() => {
        if (!deepLinkSkus || !deepLinkChannel || deepLinkPo || channelsLoading) return;
        const ch = channels.find(c => c.channelAbbreviationKey === deepLinkChannel);
        const productIds = deepLinkSkus.split(',').map(s => s.trim()).filter(Boolean);
        let planSettings: PlanSettingsOverride | null = null;
        if (deepLinkPlanSettings) {
            try {
                const parsed: unknown = JSON.parse(deepLinkPlanSettings);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) planSettings = parsed as PlanSettingsOverride;
            } catch {
                planSettings = null;
            }
        }
        if (ch && productIds.length > 0) {
            setPlanListFor(null);
            setChannelView({
                mode: 'plansNewPlan',
                params: {
                    channelId: ch.channelAbbreviationKey,
                    channelName: ch.channelName,
                    channelAbbreviationKey: ch.channelAbbreviationKey,
                    demandSource: ch.config?.demandSource ?? 'FORECAST',
                    targetDohDays: ch.config?.targetDohDays ?? null,
                    channelTierTargetDohDays: ch.config?.tierTargetDohDays ?? null,
                    isAggregated: ch.config?.isAggregated ?? false,
                    productIds,
                    autoCompute: deepLinkAutoCompute,
                    planSettings,
                },
            });
        }
        const next = new URLSearchParams(searchParams);
        next.delete('skus');
        next.delete('channel');
        next.delete('autocompute');
        next.delete('ps');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkSkus, deepLinkChannel, deepLinkPo, deepLinkPlanSettings, channelsLoading]);

    const handleDeleteDraftFromList = (plan: DistributionPlanSummary) => {
        modals.openConfirmModal({
            title: 'Delete draft plan?',
            children: (
                <Text style={{ fontSize: 14, color: '#555' }}>
                    This will permanently delete "<strong>{plan.name}</strong>" and all its transfer lines.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                try {
                    await deleteDistributionPlan(plan.id);
                    showSuccess(`Plan "${plan.name}" deleted`);
                    loadPlans();
                } catch {
                    showError('Failed to delete plan. Please try again.');
                }
            },
        });
    };

    // ── Fullscreen (channelView) render ──

    const isFullscreen = channelView.mode !== 'overview';
    const isPlanView =
        channelView.mode === 'existingPlan' ||
        channelView.mode === 'newPlan' ||
        channelView.mode === 'plansNewPlan';
    const settingsChannelKey =
        channelView.mode === 'channel' ? channelView.channel.channelAbbreviationKey :
        channelView.mode === 'poChannel' ? channelView.channel.channelAbbreviationKey :
        channelView.mode === 'newPlan' ? channelView.channel.channelAbbreviationKey :
        channelView.mode === 'plansNewPlan' ? channelView.params.channelAbbreviationKey :
        null;

    const renderFullscreen = () => {
        if (channelView.mode === 'existingPlan') {
            const chConfig = channelView.channelKey
                ? (channels.find(c => c.channelAbbreviationKey === channelView.channelKey)?.config ?? null)
                : null;
            return (
                <DistributionPlanView
                    existingPlanId={channelView.planId}
                    existingPlanName={channelView.planName}
                    fromAlertId={channelView.fromAlertId}
                    initialShipmentCode={channelView.shipmentCode}
                    channelTierTargetDohDays={chConfig?.tierTargetDohDays ?? null}
                    channelDefaultMinSourceStockUnits={chConfig?.minSourceStockUnits ?? null}
                    channelDefaultMaxOutboundUtilizationPct={chConfig?.maxOutboundUtilizationPct ?? null}
                    channelDefaultInboundInTransitDays={chConfig?.inboundInTransitDays ?? null}
                    channelDefaultSourceReserveUnits={chConfig?.sourceReserveUnits ?? null}
                    channelDefaultMinInternalDohDays={chConfig?.minInternalDohDays ?? null}
                    channelDefaultMilestone1Days={chConfig?.milestone1Days ?? null}
                    channelDefaultMilestone2Days={chConfig?.milestone2Days ?? null}
                    channelDefaultMaxUnitsPerShipment={chConfig?.maxUnitsPerShipment ?? null}
                    channelDefaultMaxMeasurementPerShipment={chConfig?.maxMeasurementPerShipment ?? null}
                    channelMeasurementUnit={chConfig?.measurementUnit ?? null}
                    onBack={() => setChannelView({ mode: 'overview' })}
                    onSaveDraft={async () => {}}
                    onDelete={handlePlanDeleted}
                />
            );
        }

        if (channelView.mode === 'plansNewPlan') {
            const chConfig = channels.find(c => c.channelAbbreviationKey === channelView.params.channelAbbreviationKey)?.config ?? null;
            return (
                <DistributionPlanView
                    params={channelView.params}
                    channelDefaultMinSourceStockUnits={chConfig?.minSourceStockUnits ?? null}
                    channelDefaultMaxOutboundUtilizationPct={chConfig?.maxOutboundUtilizationPct ?? null}
                    channelDefaultInboundInTransitDays={chConfig?.inboundInTransitDays ?? null}
                    channelDefaultSourceReserveUnits={chConfig?.sourceReserveUnits ?? null}
                    channelDefaultMinInternalDohDays={chConfig?.minInternalDohDays ?? null}
                    channelDefaultMilestone1Days={chConfig?.milestone1Days ?? null}
                    channelDefaultMilestone2Days={chConfig?.milestone2Days ?? null}
                    channelDefaultMaxUnitsPerShipment={chConfig?.maxUnitsPerShipment ?? null}
                    channelDefaultMaxMeasurementPerShipment={chConfig?.maxMeasurementPerShipment ?? null}
                    channelMeasurementUnit={chConfig?.measurementUnit ?? null}
                    onBack={() => setChannelView({ mode: 'overview' })}
                    onSaveDraft={async () => {}}
                    onDelete={handlePlanDeleted}
                />
            );
        }

        if (channelView.mode === 'newPlan') {
            const ch = channelView.channel;
            const chConfig = channels.find(c => c.channelAbbreviationKey === ch.channelAbbreviationKey)?.config ?? null;
            return (
                <DistributionPlanView
                    params={channelView.params}
                    channelDefaultMinSourceStockUnits={chConfig?.minSourceStockUnits ?? null}
                    channelDefaultMaxOutboundUtilizationPct={chConfig?.maxOutboundUtilizationPct ?? null}
                    channelDefaultInboundInTransitDays={chConfig?.inboundInTransitDays ?? null}
                    channelDefaultSourceReserveUnits={chConfig?.sourceReserveUnits ?? null}
                    channelDefaultMinInternalDohDays={chConfig?.minInternalDohDays ?? null}
                    channelDefaultMilestone1Days={chConfig?.milestone1Days ?? null}
                    channelDefaultMilestone2Days={chConfig?.milestone2Days ?? null}
                    channelDefaultMaxUnitsPerShipment={chConfig?.maxUnitsPerShipment ?? null}
                    channelDefaultMaxMeasurementPerShipment={chConfig?.maxMeasurementPerShipment ?? null}
                    channelMeasurementUnit={chConfig?.measurementUnit ?? null}
                    onBack={() => setChannelView({ mode: 'channel', channel: ch })}
                    onSaveDraft={async () => {}}
                    onDelete={handlePlanDeleted}
                />
            );
        }

        if (channelView.mode === 'channel') {
            const ch = channelView.channel;
            return (
                <ChannelDetailView
                    initialData={ch}
                    onBack={() => setChannelView({ mode: 'overview' })}
                    onComputePlan={planParams =>
                        setChannelView({
                            mode: 'newPlan',
                            channel: ch,
                            params: {
                                channelId: ch.channelId,
                                channelName: ch.channelName,
                                channelAbbreviationKey: ch.channelAbbreviationKey,
                                demandSource: planParams.demandSource,
                                targetDohDays: planParams.targetDohDays,
                                channelTierTargetDohDays: channels.find(c => c.channelAbbreviationKey === ch.channelAbbreviationKey)?.config?.tierTargetDohDays ?? null,
                                isAggregated: ch.isAggregated,
                            },
                        })
                    }
                    onOpenPlan={(planId, planName) => openExistingPlan(planId, planName, ch.channelAbbreviationKey)}
                />
            );
        }

        if (channelView.mode === 'poChannel') {
            return (
                <PoChannelDetailView
                    channel={channelView.channel}
                    onBack={() => setChannelView({ mode: 'overview' })}
                    onCreatePlanFromPo={handleCreatePlanFromPo}
                />
            );
        }

        return null;
    };

    // ── Plan tab body (sub-tabs or plan-list screen) ──

    const renderPlanBody = () => {
        if (planListFor === 'push') {
            return (
                <PlanListView
                    title="All push-channel plans"
                    channels={pushChannels}
                    plans={pushDrafts}
                    loading={plansLoading}
                    selectedChannelKey={newPlanChannelKey}
                    onSelectChannel={setNewPlanChannelKey}
                    onStartPlan={handleStartNewPlanFromTab}
                    onOpenPlan={p => openExistingPlan(p.id, p.name, p.channelAbbreviationKey)}
                    onDeleteDraft={handleDeleteDraftFromList}
                    onBack={() => setPlanListFor(null)}
                />
            );
        }
        if (planListFor === 'poDriven') {
            return (
                <PlanListView
                    title="All PO-driven plans"
                    channels={poChannels}
                    plans={poDrafts}
                    loading={plansLoading}
                    selectedChannelKey={newPlanChannelKey}
                    onSelectChannel={setNewPlanChannelKey}
                    onStartPlan={handleStartNewPlanFromTab}
                    onOpenPlan={p => openExistingPlan(p.id, p.name, p.channelAbbreviationKey)}
                    onDeleteDraft={handleDeleteDraftFromList}
                    onBack={() => setPlanListFor(null)}
                />
            );
        }

        return (
            <Tabs
                value={planSubTab}
                onChange={v => setPlanSubTab(v as PlanSubTab)}
                color="#6366f1"
                style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
                <Tabs.List>
                    <Tabs.Tab value="push">Push Channels</Tabs.Tab>
                    <Tabs.Tab value="poDriven">PO Driven Channels</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="push" pt="md" style={{ flex: 1, overflowY: 'auto' }}>
                    <DistributionOverview
                        onComputePlan={() => { setNewPlanChannelKey(null); setPlanListFor('push'); }}
                        onShowAllPlans={() => { setNewPlanChannelKey(null); setPlanListFor('push'); }}
                        onViewPlan={plan => openExistingPlan(plan.id, plan.name, plan.channelAbbreviationKey)}
                        onChannelClick={ch => setChannelView({ mode: 'channel', channel: ch })}
                        draftPlans={pushDrafts}
                        draftPlansLoading={plansLoading}
                        poChannelKeys={poChannelKeys}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="poDriven" pt="md" style={{ flex: 1, overflowY: 'auto' }}>
                    <PoDrivenOverview
                        poChannels={poChannels}
                        poChannelKeys={poChannelKeys}
                        draftPlans={poDrafts}
                        draftPlansLoading={plansLoading}
                        onChannelClick={ch => setChannelView({ mode: 'poChannel', channel: ch })}
                        onComputePlan={() => { setNewPlanChannelKey(null); setPlanListFor('poDriven'); }}
                        onShowAllPlans={() => { setNewPlanChannelKey(null); setPlanListFor('poDriven'); }}
                        onViewPlan={plan => openExistingPlan(plan.id, plan.name, plan.channelAbbreviationKey)}
                    />
                </Tabs.Panel>
            </Tabs>
        );
    };

    return (
        <div className="flex h-full w-full flex-col page-shell">
            {!isPlanView && (
                <PageHeader
                    icon={<Package size={20} weight="duotone" />}
                    title="Distribution"
                    subtitle="Move stock from internal warehouses to channel warehouses to meet forward demand"
                    actions={
                        <Group gap={8} wrap="nowrap">
                            {!isFullscreen && (
                                <SegmentedControl
                                    size="xs"
                                    value={topTab}
                                    onChange={v => switchMode(v as TopTab)}
                                    data={[
                                        { value: 'plan', label: 'Plan Mode' },
                                        { value: 'track', label: 'Track Mode' },
                                    ]}
                                />
                            )}
                            <Tooltip label={settingsChannelKey ? `${settingsChannelKey} settings` : 'Distribution settings'} withArrow>
                                <ActionIcon variant="subtle" color="dark" size="md" onClick={openSettings}>
                                    <GearSix size={18} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    }
                />
            )}

            {isFullscreen ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginTop: isPlanView ? 0 : -16 }}>
                    {renderFullscreen()}
                </div>
            ) : topTab === 'plan' ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto', marginTop: 16 }}>
                    {renderPlanBody()}
                </div>
            ) : (
                <div style={{ flex: 1, overflowY: 'auto', marginTop: 16 }}>
                    <TrackView onOpenSaleOrder={so => openExistingPlan(so.planId, so.planName, so.channelAbbreviationKey, so.saleOrderCode)} />
                </div>
            )}

            <Modal
                opened={settingsOpen}
                onClose={() => {
                    closeSettings();
                    getDistributionChannels().then(setChannels).catch(() => {});
                }}
                title={settingsChannelKey ? `${settingsChannelKey} — Channel Settings` : 'Distribution Settings'}
                size="70rem"
            >
                <DistributionPathsTab initialChannelKey={settingsChannelKey ?? undefined} />
            </Modal>
        </div>
    );
};

export default DistributionContainer;
